import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const generateDeviceFingerprint = () => {
  const ua = navigator?.userAgent || '';
  const lang = navigator?.language || '';
  const tz = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || '';
  const screen = `${window?.screen?.width}x${window?.screen?.height}`;
  const raw = `${ua}|${lang}|${tz}|${screen}`;
  // Simple hash
  let hash = 0;
  for (let i = 0; i < raw?.length; i++) {
    const char = raw?.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return `fp_${Math.abs(hash)?.toString(36)}_${raw?.length?.toString(36)}`;
};

const generate6DigitCode = () =>
  Math.floor(100000 + Math.random() * 900000)?.toString();

// ─── Component ────────────────────────────────────────────────────────────────

const TwoFactorModal = ({ userId, twoFASettings, onVerified, onCancel }) => {
  const [step, setStep] = useState('verify'); // 'verify' | 'backup'
  const [code, setCode] = useState('');
  const [sentCode, setSentCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [backupCode, setBackupCode] = useState('');
  const [backupError, setBackupError] = useState('');
  const [backupLoading, setBackupLoading] = useState(false);
  const cooldownRef = useRef(null);

  const method = twoFASettings?.method || 'email';
  const fingerprint = generateDeviceFingerprint();

  // Auto-send code on mount for email method
  useEffect(() => {
    if (method === 'email') {
      handleSendCode();
    }
    return () => {
      if (cooldownRef?.current) clearInterval(cooldownRef?.current);
    };
  }, []);

  const startCooldown = () => {
    setCooldown(60);
    cooldownRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(cooldownRef?.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendCode = async () => {
    setLoading(true);
    setError('');
    const newCode = generate6DigitCode();
    setSentCode(newCode);
    // Simulate sending (in production: wire to Supabase email / Twilio SMS)
    await new Promise(r => setTimeout(r, 800));
    setCodeSent(true);
    setLoading(false);
    startCooldown();
    // In dev: log code so it can be tested
    if (import.meta.env?.DEV) {
      console.info('[2FA] Verification code:', newCode);
    }
  };

  const handleVerify = async () => {
    if (!code?.trim()) {
      setError('Veuillez entrer le code de vérification.');
      return;
    }
    if (code?.trim() !== sentCode) {
      setError('Code incorrect. Veuillez réessayer.');
      return;
    }
    setLoading(true);
    setError('');

    try {
      // If user wants to trust this device, store fingerprint
      if (trustDevice) {
        await supabase?.from('trusted_devices')?.upsert({
          user_id: userId,
          device_fingerprint: fingerprint,
          device_name: navigator?.userAgent?.substring(0, 80) || 'Appareil inconnu',
          device_type: /Mobile|Android|iPhone/i?.test(navigator?.userAgent) ? 'mobile' : 'desktop',
          browser: navigator?.userAgent?.match(/(Chrome|Firefox|Safari|Edge|Opera)[\/\s][\d.]+/)?.[0] || 'Navigateur inconnu',
          os: navigator?.platform || 'OS inconnu',
          ip_address: null,
          trusted_at: new Date()?.toISOString(),
          last_seen_at: new Date()?.toISOString(),
          is_active: true,
        }, { onConflict: 'user_id,device_fingerprint' });
      }
      onVerified();
    } catch (err) {
      console.error('2FA trust device error:', err);
      // Still allow login even if trust storage fails
      onVerified();
    } finally {
      setLoading(false);
    }
  };

  const handleBackupCode = async () => {
    if (!backupCode?.trim()) {
      setBackupError('Veuillez entrer un code de récupération.');
      return;
    }
    setBackupLoading(true);
    setBackupError('');

    try {
      // Fetch current backup codes from DB
      const { data, error: fetchErr } = await supabase
        ?.from('user_2fa_settings')
        ?.select('backup_codes, backup_codes_used')
        ?.eq('user_id', userId)
        ?.single();

      if (fetchErr || !data) {
        setBackupError('Impossible de vérifier le code. Réessayez.');
        setBackupLoading(false);
        return;
      }

      const storedCodes = data?.backup_codes || [];
      const usedCodes = data?.backup_codes_used || [];
      const normalizedInput = backupCode?.trim()?.toLowerCase();

      const matchedCode = storedCodes?.find(
        c => c?.toLowerCase() === normalizedInput && !usedCodes?.includes(c)
      );

      if (!matchedCode) {
        setBackupError('Code de récupération invalide ou déjà utilisé.');
        setBackupLoading(false);
        return;
      }

      // Mark code as used
      await supabase
        ?.from('user_2fa_settings')
        ?.update({ backup_codes_used: [...usedCodes, matchedCode] })
        ?.eq('user_id', userId);

      onVerified();
    } catch (err) {
      console.error('Backup code error:', err);
      setBackupError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setBackupLoading(false);
    }
  };

  const codeInputs = code?.split('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div className="w-full max-w-md bg-card rounded-2xl shadow-ios-large border border-border overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-gold/20 to-brand-earth/10 flex items-center justify-center">
              <Icon name="ShieldCheck" size={22} className="text-brand-earth" />
            </div>
            <button
              onClick={onCancel}
              className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={18} />
            </button>
          </div>
          <h2 className="text-ios-title2 font-display font-display-semibold text-brand-earth leading-tight">
            Vérification en deux étapes
          </h2>
          <p className="text-ios-callout text-text-secondary mt-1 font-system">
            {step === 'verify'
              ? method === 'email' ?'Un code de vérification a été envoyé à votre adresse email.'
                : `Un code de vérification a été envoyé par SMS au ${twoFASettings?.phone_number || 'votre téléphone'}.`
              : 'Entrez l\'un de vos codes de récupération de secours.'}
          </p>
        </div>

        <div className="px-6 py-6 space-y-4">
          {step === 'verify' ? (
            <>
              {/* Method badge */}
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-body-medium ${
                  method === 'email' ?'bg-blue-50 text-blue-700 border border-blue-100' :'bg-purple-50 text-purple-700 border border-purple-100'
                }`}>
                  <Icon name={method === 'email' ? 'Mail' : 'Smartphone'} size={11} />
                  {method === 'email' ? 'Email' : 'SMS'}
                </span>
                {codeSent && (
                  <span className="text-[11px] text-green-600 font-body-medium flex items-center gap-1">
                    <Icon name="CheckCircle" size={11} />
                    Code envoyé
                  </span>
                )}
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-start gap-2.5 p-3 bg-red-50 border border-red-100 rounded-lg">
                  <Icon name="AlertCircle" size={15} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-ios-caption1 text-red-600 font-system">{error}</p>
                </div>
              )}

              {/* Code input */}
              <div>
                <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-2">
                  Code de vérification
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Icon name="KeyRound" size={16} className="text-text-secondary" />
                  </div>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={e => {
                      setCode(e?.target?.value?.replace(/\D/g, '')?.slice(0, 6));
                      setError('');
                    }}
                    onKeyDown={e => { if (e?.key === 'Enter') handleVerify(); }}
                    placeholder="000000"
                    className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg text-ios-callout font-system text-text-primary tracking-widest focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all duration-200 bg-muted/20"
                    autoFocus
                  />
                </div>
              </div>

              {/* Trust device checkbox */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <div
                  onClick={() => setTrustDevice(v => !v)}
                  className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                    trustDevice
                      ? 'bg-brand-earth border-brand-earth' :'border-border group-hover:border-brand-gold'
                  }`}
                >
                  {trustDevice && <Icon name="Check" size={12} color="white" strokeWidth={3} />}
                </div>
                <div>
                  <p className="text-ios-callout font-system font-body-medium text-text-primary">
                    Faire confiance à cet appareil
                  </p>
                  <p className="text-ios-caption1 text-text-secondary mt-0.5">
                    Ne plus demander de code 2FA sur cet appareil pendant 30 jours.
                  </p>
                </div>
              </label>

              {/* Verify button */}
              <button
                onClick={handleVerify}
                disabled={loading || code?.length < 6}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-brand-earth to-brand-earth/90 text-white rounded-lg text-ios-callout font-system font-body-semibold hover:from-brand-earth/95 hover:to-brand-earth/85 disabled:opacity-60 transition-all duration-200 shadow-sm"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Icon name="ShieldCheck" size={17} color="white" />
                    <span>Vérifier</span>
                  </>
                )}
              </button>

              {/* Resend */}
              <div className="flex items-center justify-between pt-1">
                <button
                  onClick={handleSendCode}
                  disabled={cooldown > 0 || loading}
                  className="text-ios-caption1 text-brand-earth hover:underline font-system font-body-medium disabled:text-text-secondary disabled:no-underline transition-colors"
                >
                  {cooldown > 0 ? `Renvoyer dans ${cooldown}s` : 'Renvoyer le code'}
                </button>
                <button
                  onClick={() => { setStep('backup'); setError(''); }}
                  className="text-ios-caption1 text-text-secondary hover:text-brand-earth font-system transition-colors"
                >
                  Utiliser un code de secours
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Backup code step */}
              <button
                onClick={() => { setStep('verify'); setBackupError(''); setBackupCode(''); }}
                className="flex items-center gap-1.5 text-ios-caption1 text-text-secondary hover:text-brand-earth font-system transition-colors"
              >
                <Icon name="ArrowLeft" size={13} />
                Retour à la vérification
              </button>

              {backupError && (
                <div className="flex items-start gap-2.5 p-3 bg-red-50 border border-red-100 rounded-lg">
                  <Icon name="AlertCircle" size={15} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-ios-caption1 text-red-600 font-system">{backupError}</p>
                </div>
              )}

              <div>
                <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-2">
                  Code de récupération
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <Icon name="Key" size={16} className="text-text-secondary" />
                  </div>
                  <input
                    type="text"
                    value={backupCode}
                    onChange={e => { setBackupCode(e?.target?.value); setBackupError(''); }}
                    onKeyDown={e => { if (e?.key === 'Enter') handleBackupCode(); }}
                    placeholder="XXXX-XXXX"
                    className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg text-ios-callout font-system text-text-primary tracking-wider focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all duration-200 bg-muted/20"
                    autoFocus
                  />
                </div>
                <p className="text-ios-caption1 text-text-secondary mt-1.5 font-system">
                  Format : XXXX-XXXX. Chaque code ne peut être utilisé qu'une seule fois.
                </p>
              </div>

              <button
                onClick={handleBackupCode}
                disabled={backupLoading || !backupCode?.trim()}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-brand-earth to-brand-earth/90 text-white rounded-lg text-ios-callout font-system font-body-semibold hover:from-brand-earth/95 hover:to-brand-earth/85 disabled:opacity-60 transition-all duration-200 shadow-sm"
              >
                {backupLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Icon name="Key" size={17} color="white" />
                    <span>Utiliser ce code</span>
                  </>
                )}
              </button>
            </>
          )}
        </div>

        {/* Footer note */}
        <div className="px-6 pb-6">
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-lg">
            <Icon name="Info" size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-700 font-system leading-relaxed">
              Cette vérification protège votre compte contre les accès non autorisés. Vous pouvez gérer vos appareils de confiance dans les paramètres de sécurité.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorModal;
